import { useState, useMemo } from "react";
import { Card } from "../common/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../common/Table";
import { Badge } from "../common/Badge";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { Button } from "../common/Button";

export function RevenueTable({ transactionData = [], isLoading }) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const normalizedData = useMemo(() => {
    return transactionData.map((tx) => ({
      date: new Date(tx.createdAt).toLocaleDateString(),
      reason: tx.reason,
      bookingRef: tx.transactionRef,
      amount: `${tx.currency} ${tx.amount.toLocaleString()}`,
      status: tx.status,
    }));
  }, [transactionData]);

  const totalPages = Math.ceil(normalizedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = normalizedData.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "Success":
        return "bg-green-100 text-green-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Card className="bg-white rounded-lg shadow-sm border border-gray-100">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F8F9FC]">
              <TableHead>Date</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Booking Ref</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Markup</TableHead>
              <TableHead>Commission</TableHead>
              <TableHead>Service Fee</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isLoading
              ? Array.from({ length: itemsPerPage }).map((_, idx) => (
                  <TableRow key={idx} className="animate-pulse">
                    {Array.from({ length: 8 }).map((__, cellIdx) => (
                      <TableCell key={cellIdx}>
                        <div className="h-4 bg-gray-200 rounded w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : currentData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>{row.reason}</TableCell>
                    <TableCell className="text-[#1E61DC]">
                      {row.bookingRef}
                    </TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>
                      <Badge
                        className={getStatusColor(row.status)}
                        variant="secondary"
                      >
                        {row.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>

      {!isLoading && normalizedData.length > 0 && (
        <div className="flex justify-between px-6 py-4 border-t">
          <div>
            Showing {startIndex + 1} to{" "}
            {Math.min(startIndex + itemsPerPage, normalizedData.length)} of{" "}
            {normalizedData.length}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <HiChevronLeft />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
            >
              <HiChevronRight />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
